import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function FunctionalTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Teal Header */}
          <div className={`bg-gradient-to-r ${colors.headerBg} ${colors.headerText} p-8`}>
            <h1 className={`text-4xl font-bold mb-2 ${colors.sectionTitle}`}>{personalInfo.fullName || 'YOUR NAME'}</h1>
            <div className="text-lg mb-3">{personalInfo.email}</div>
            <div className="flex flex-wrap gap-4 text-sm">
              {personalInfo.phone && <span>• {personalInfo.phone}</span>}
              {personalInfo.address && <span>• {personalInfo.address}</span>}
              {personalInfo.linkedin && <span>• {personalInfo.linkedin}</span>}
            </div>
          </div>

          <div className="p-8">
            {/* Core Competencies - Featured First */}
            {skills && skills.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder}`}>
                  CORE COMPETENCIES
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx} className={`${colors.accentBg} border-l-4 ${colors.accentBorder} p-3`}>
                      <div className={`text-sm font-bold ${colors.sectionTitle} mb-2`}>{skillGroup.category}</div>
                      <div className="text-xs text-gray-700 space-y-1">
                        {skillGroup.skills?.slice(0, 4).map((skill: string, i: number) => (
                          <div key={i}>• {skill}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Professional Summary */}
            {summary && (
              <div className="mb-6">
                <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>
                  {labels.professionalSummary}
                </h2>
                <p className="text-gray-700 leading-relaxed">{summary}</p>
              </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 pb-2 border-b-2 ${colors.sectionBorder}`}>
                  {labels.workExperience}
                </h2>
                {experience.map((exp: any) => (
                  <div key={exp.id} className={`mb-4 pl-4 border-l-2 ${colors.timelineBorder}`}>
                    <div className="flex justify-between items-baseline mb-1">
                      <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{exp.jobTitle}</h3>
                      <span className="text-sm text-gray-600">{exp.startDate} - {exp.endDate || labels.present}</span>
                    </div>
                    <div className={`text-sm font-semibold ${colors.accentText} mb-2`}>{exp.company}</div>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{exp.description}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Education */}
            {education && education.length > 0 && (
              <div>
                <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>
                  {labels.education}
                </h2>
                {education.map((edu: any) => (
                  <div key={edu.id} className="mb-3">
                    <div className={`text-base font-bold ${colors.sectionTitle}`}>{edu.degree}</div>
                    <div className={`text-sm ${colors.accentText}`}>{edu.institution}</div>
                    <div className="text-sm text-gray-600">{edu.graduationDate}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
